<template>
    <div v-if="app.page.lang === 'ru'" class="fast-search">
        <button
            type="button"
            class="fast-search__button"
            :aria-expanded="isSearchShown.toString()"
            :aria-label="isSearchShown ? 'Закрыть поиск' : 'Открыть поиск'"
            aria-controls="fast-search-form"
            @click="isSearchShown = !isSearchShown"
        >
            <z-icon
                :name="isSearchShown ? 'tools/close' : 'tools/magnifier'"
                width="24"
                height="24" />
        </button>
        <transition name="search-transition">
            <div
                v-if="isSearchShown"
                class="fast-search__wrapper"
                aria-label="Форма поиска"
            >
                <div class="fast-search__container container">
                    <div class="fast-search__form">
                        <z-input
                            v-model="params.q"
                            :placeholder="text.inputPlaceholder"
                            :min-length="minSearchLength"
                            :aria-label="text.inputPlaceholder"
                            ref="input"
                            icon="tools/magnifier"
                            icon-position="left"
                            class="fast-search__input"
                            is-filter
                            @input="onInput"
                            @clear="onClear"
                        />

                    </div>
                    <div class="search-page__preloader">
                        <z-preloader v-if="isLoading" />
                    </div>
                    <div v-if="params.q && params.q.length >= minSearchLength" class="fast-search__results">
                        <div
                            v-for="(item, index) in data?.items"
                            class="fast-search__results-item"
                            :key="`search-${index}-${item.title}`"
                        >
                            <z-link
                                :href="item.url"
                                size="xl"
                                class="fast-search__results-item-link"
                                :underlined="false"
                                :aria-label="item.title"
                            >
                                <span v-html="item.title"></span>
                            </z-link>
                            <p v-if="item.text" class="text-size-l u-bottom-margin--xs" v-html="item.text"/>
                            <z-date theme="simple" :from="item.date" />
                        </div>
                        <z-link
                            v-if="length > 3"
                            :href="`/search/?q=${params.q}`"
                            class="fast-search__results-link"
                        >
                            {{ text.buttonText }}
                        </z-link>
                        <z-not-found v-show="!isLoading && !data?.items.length" >
                            <template v-slot:description>
                                <div
                                    class="u-bottom-margin--2xs"
                                    v-html="text.description"
                                    role="status"
                                    aria-live="polite"
                                ></div>
                            </template>
                        </z-not-found>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>
<script setup>
import { ref } from 'vue'
import {localize} from '@/utils/i18n';
import { useGetter } from '@/composable/useGetter'
import { getSearchData } from '@/api/search'
import {debounce} from 'throttle-debounce';

const { app } = useGetter(['app'])
const params = ref({
    page: 1,
    q: '',
    how: 'r',
})

const isSearchShown = ref(false)
const input = ref(null)
const isLoading = ref(false)
const errorMessage = ref('')
const data = ref(null)
const length = ref(0)
const text = {
    inputPlaceholder: localize({
        ru: 'Поиск',
        en: 'Search',
        cn: 'Search'
    }),
    buttonText: localize({
        ru: 'Все результаты',
        en: 'Show all',
        cn: 'Show all'
    }),
    description: localize({
        ru: 'По&nbsp;Вашему запросу ничего не&nbsp;нашлось.',
        en: 'Sorry, no matches were found for your request. Try changing your search terms.',
        cn: 'Sorry, no matches were found for your request. Try changing your search terms.'
    }),
    resetFilters: localize({
        ru: 'Сбросить фильтры',
        en: 'Reset filters',
        cn: 'Reset filters'
    }),
    errorMessage: localize({
        ru: 'Произошла ошибка попробуйте еще раз',
        en: 'An error occurred, please try again',
        cn: 'An error occurred, please try again'
    })
}
const minSearchLength = 3

const onInput = debounce(500, function () {
    if (input.value.isValid) fetchData()
})

const onClear = () => {
    params.value.q = ''
}

function fetchData (){
    isLoading.value = true

    const request = debounce(300, async () => {
        try {
            data.value = await getSearchData(params.value)
            length.value = 0

            if (data.value.items && data.value.items.length) {
                length.value = data.value.items.length
                data.value.items = data.value.items.slice(0, 3)
            }
        }
        catch {
            errorMessage.value = text.errorMessage
        }
        finally {
            isLoading.value = false
        }
    })

    request()
}
</script>

<style lang="scss">
.fast-search{
    display: inline-block;
    &__button {
        position: relative;
        display: inline-block;
        padding: 8px;
        vertical-align: text-bottom;
        border-radius: 50%;
        border: unset;
        background-color: rgba(255, 255, 255, 0.2);
        z-index: 11;

        svg {
            display: block;
            path {
                fill: var(--color-white);
            }
        }
    }

    &__wrapper{
        position: absolute;
        top: 0;
        right: 0;
        padding-top: 120px;
        width: 100%;
        border-radius: 0 0 60px 60px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.20);
        background: var(--color-primary);
        z-index: 1;
        overflow: hidden;

        &:before{
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(180deg, rgba(2, 105, 88, 0.00) 42.08%, rgba(2, 105, 88, 0.50) 100%);
            mix-blend-mode: multiply;
            pointer-events: none;
        }
    }

    &__container{
        padding-top: $token-grids-desktop-offset;
        padding-bottom: $token-grids-desktop-offset;
        max-height: calc(100vh - 120px);
        display: flex;
        flex-direction: column;

        @include breakpoint(laptop) {
            padding-top: $token-grids-laptop-offset;
            padding-bottom: $token-grids-laptop-offset;
        }

        @include breakpoint(tablet) {
            padding-top: $token-grids-tablet-offset;
            padding-bottom: $token-grids-tablet-offset;
        }

        @include breakpoint(v-tablet) {
            padding-top: $token-grids-v-tablet-offset;
            padding-bottom: $token-grids-v-tablet-offset;
        }

        @include breakpoint(mobile) {
            padding-top: $token-grids-mobile-offset;
            padding-bottom: $token-grids-mobile-offset;
        }
    }

    &__form{
        .z-input__input {
            color: var(--color-black);
        }
    }

    &__results{
        @include margin-level(top, M);
        max-height: 100%;
        overflow-y: auto;

        @extend %scrollbar;

        &-link{
            width: fit-content;
            display: flex;
            justify-content: center;
            font-size: var(--typography-l);
            color: var(--color-white);
        }
    }

    &__input{
        @include typo-level(L);
    }

    &__results-item{
        &:not(:last-of-type){
            @include margin-level(bottom, M);
        }

        &-link{
            color: var(--color-white);
        }

        & p {
            font-size: var(--typography-m);
            color: var(--color-white);
        }

        & .z-date{
            --font-color: var(--color-white);
            --font-color-simple: var(--color-white);
            color: var(--color-white);
            opacity: 0.8;
        }
    }
}
.search-transition-enter-active,
.search-transition-leave-active {
    transition: max-height 0.6s ease, width 0.4s ease;
}
.search-transition-enter-to,
.search-transition-leave-from{
    width: 100%;
    max-height: 435px;

}
.search-transition-enter-from,
.search-transition-leave-to {
    width: 0;
    max-height: 0;
}
</style>
